import React, { useState, useEffect } from "react";
import "./PracticePage.css";
import { apiGet, apiPost, apiPut } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Session, Scenario, PersonaType, CoachingPlan } from "../../types";
import { ScenarioBrowser } from "../roleplay/ScenarioBrowser";
import { TextChat } from "../roleplay/TextChat";
import { VoiceRolePlay } from "../roleplay/voice";
import { UploadTranscript } from "../coaching/UploadTranscript";
import { SessionDetailPanel } from "../shared/SessionDetailPanel";
import { Breadcrumb } from "../shared/Breadcrumb";
import { Button } from "../shared/FormField";

type PracticeView = "home" | "roleplay" | "session-detail";

export function PracticePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedScenarioId = searchParams.get("scenario") || undefined;

  const [view, setView] = useState<PracticeView>("home");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [coachingPlan, setCoachingPlan] = useState<CoachingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [activeVoiceId, setActiveVoiceId] = useState("matthew");
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [sessionsResult, planResult] = await Promise.all([
        apiGet<{ items: Session[] }>("/api/sessions"),
        apiGet<CoachingPlan | { plan: null }>("/api/coaching/plan"),
      ]);
      setSessions(sessionsResult.items);
      if (planResult && !("plan" in planResult && planResult.plan === null)) {
        setCoachingPlan(planResult as CoachingPlan);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async (scenarioId: string, personaType: PersonaType, mode: "text" | "voice", voiceId?: string) => {
    try {
      const session = await apiPost<Session>("/api/sessions", { scenarioId, personaType, mode });
      setActiveSession(session);
      if (voiceId) setActiveVoiceId(voiceId);
      setView("roleplay");
      // Clear scenario param once session starts
      if (preselectedScenarioId) setSearchParams({});
    } catch (err) {
      console.error("Failed to start session:", err);
    }
  };

  const handleSessionEnd = () => {
    setActiveSession(null);
    setView("home");
    loadData();
  };

  const completedSessions = sessions.filter((s) => s.status === "completed");
  const scoredSessions = completedSessions.filter((s) => s.score);
  const avgScore = scoredSessions.length > 0
    ? Math.round(scoredSessions.reduce((sum, s) => sum + (s.score?.overallScore || 0), 0) / scoredSessions.length)
    : null;
  const recentSessions = sessions.slice(0, 8);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
  const formatDuration = (ms: number) => {
    if (!ms) return "—";
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  // Active role-play view
  if (view === "roleplay" && activeSession) {
    return (
      <div className="protrain-practice">
        <Breadcrumb items={[
          { label: "Practice", path: "/practice" },
          { label: `${activeSession.mode === "text" ? "Text" : "Voice"} Role-Play` },
        ]} />
        <div className="protrain-practice-session-bar">
          <button className="protrain-practice-back" onClick={handleSessionEnd}>← Back</button>
          <span>Mode: {activeSession.mode} · Persona: {activeSession.personaType}</span>
        </div>
        {activeSession.mode === "text" ? (
          <TextChat sessionId={activeSession.sessionId} onSessionEnd={handleSessionEnd} />
        ) : (
          <VoiceRolePlay
            config={{
              wsUrl: import.meta.env.VITE_VOICE_WS_URL || `wss://${window.location.host}/ws`,
              sessionId: activeSession.sessionId,
              orgId: activeSession.orgId,
              userId: activeSession.userId,
              persona: activeSession.personaType,
              scenarioName: "",
              scenarioDesc: "",
              voiceId: activeVoiceId,
              onSessionEnd: async (transcript) => {
                try { await apiPut(`/api/sessions/${activeSession.sessionId}/end`, { transcript }); } catch (err) {
                  console.error("Failed to end voice session:", err);
                }
                handleSessionEnd();
              },
            }}
          />
        )}
      </div>
    );
  }

  if (loading) return <div className="protrain-practice-loading">Loading...</div>;

  return (
    <div className="protrain-practice">
      {/* Quick stats */}
      <div className="protrain-practice-header">
        <div>
          <h2 className="protrain-practice-title">Welcome back, {user?.name?.split(" ")[0] || "there"}</h2>
          <p className="protrain-practice-subtitle">Practice makes perfect</p>
        </div>
        <Button onClick={() => setUploadOpen(true)}>📄 Upload Transcript</Button>
      </div>

      <div className="protrain-practice-stats">
        <div className="protrain-practice-stat">
          <span className="protrain-practice-stat-value">{completedSessions.length}</span>
          <span className="protrain-practice-stat-label">Sessions</span>
        </div>
        <div className="protrain-practice-stat">
          <span className="protrain-practice-stat-value">{avgScore ?? "—"}</span>
          <span className="protrain-practice-stat-label">Avg Score</span>
        </div>
        <div className="protrain-practice-stat">
          <span className="protrain-practice-stat-value">{coachingPlan?.strengthScore ?? "—"}</span>
          <span className="protrain-practice-stat-label">Strength</span>
        </div>
      </div>

      {/* Scenario browser */}
      <div className="protrain-practice-section">
        <h3>Start a Practice Session</h3>
        <ScenarioBrowser onStartSession={handleStartSession} preselectedScenarioId={preselectedScenarioId} />
      </div>

      {/* Recent sessions */}
      <div className="protrain-practice-section">
        <h3>Recent Sessions</h3>
        {recentSessions.length === 0 ? (
          <p className="protrain-practice-empty">No sessions yet. Start a role-play or upload a transcript above.</p>
        ) : (
          <div className="protrain-practice-sessions">
            {recentSessions.map((s) => (
              <button key={s.sessionId} className="protrain-practice-session-card" onClick={() => setSelectedSession(s)}>
                <div className="protrain-practice-session-top">
                  <span>{s.mode === "text" ? "💬" : s.mode === "transcript" ? "📄" : "🎤"}</span>
                  <span className="protrain-practice-session-date">{formatDate(s.createdAt)}</span>
                  <span className="protrain-practice-session-persona">{s.personaType}</span>
                </div>
                <div className="protrain-practice-session-bottom">
                  <span>{formatDuration(s.durationMs)}</span>
                  {s.score ? (
                    <span className={`protrain-practice-score ${s.score.overallScore >= 80 ? "protrain-practice-score--high" : s.score.overallScore >= 60 ? "protrain-practice-score--mid" : "protrain-practice-score--low"}`}>
                      {s.score.overallScore}
                    </span>
                  ) : (
                    <span className="protrain-practice-score protrain-practice-score--none">—</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <SessionDetailPanel
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onPracticeAgain={(scenarioId) => {
          setSelectedSession(null);
          setSearchParams({ scenario: scenarioId });
        }}
      />

      <UploadTranscript
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(session) => {
          setSessions((prev) => [session, ...prev]);
          setSelectedSession(session);
        }}
      />
    </div>
  );
}
